import { useCallback, useMemo, useState } from "react";
import { PollingContext } from "./context";

export const PollingContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [pollingValues, setPollingValuesState] = useState<Map<string, string>>(
    new Map(),
  );
  const [cachedData, setCachedDataState] = useState<Map<string, any>>(
    new Map(),
  );

  const setPollingValue = useCallback(
    (args: { key: string; value: string }) => {
      setPollingValuesState((prev) => {
        const newMap = new Map(prev);
        newMap.set(args.key, args.value);
        return newMap;
      });
    },
    [],
  );

  const setPollingValues = useCallback(
    (args: { key: string; value: string }[]) => {
      setPollingValuesState((prev) => {
        const newMap = new Map(prev);
        args.forEach(({ key, value }) => newMap.set(key, value));
        return newMap;
      });
    },
    [],
  );

  const getPollingValue = useCallback(
    (key: string) => {
      return pollingValues.get(key) ?? null;
    },
    [pollingValues],
  );

  const getPollingValues = useCallback(() => {
    return Object.fromEntries(pollingValues.entries());
  }, [pollingValues]);

  const clearPollingValue = useCallback((key: string) => {
    setPollingValuesState((prev) => {
      const newMap = new Map(prev);
      newMap.delete(key);
      return newMap;
    });
  }, []);

  const setCachedData = useCallback((args: { key: string; data: any }) => {
    setCachedDataState((prev) => {
      const newMap = new Map(prev);
      newMap.set(args.key, args.data);
      return newMap;
    });
  }, []);

  const getCachedData = useCallback(
    (key: string) => {
      return cachedData.get(key) ?? null;
    },
    [cachedData],
  );

  const getAllCachedData = useCallback(
    () => Object.fromEntries(cachedData.entries()),
    [cachedData],
  );

  const isAnyPolling = useMemo(() => {
    return pollingValues.size > 0;
  }, [pollingValues]);

  const isAnyCached = useMemo(() => {
    return cachedData.size > 0;
  }, [cachedData]);

  return (
    <PollingContext.Provider
      value={{
        setPollingValue,
        setPollingValues,
        getPollingValue,
        getPollingValues,
        clearPollingValue,
        setCachedData,
        getCachedData,
        getAllCachedData,
        isAnyPolling,
        isAnyCached,
      }}
    >
      {children}
    </PollingContext.Provider>
  );
};
